import React from 'react';
import { X, Copy, ExternalLink, CheckCircle, XCircle, Clock, AlertCircle, CreditCard, User, Mail, Calendar, Database, ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';

const TransactionDetailsModal = ({ transaction, onClose }) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const transactionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - transactionDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />;
      case 'PENDING':
        return <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />;
      default:
        return <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'PENDING':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'FAILED':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`, {
      duration: 2000,
      position: 'top-right',
    });
  };

  const renderMetadata = (metadata) => {
    if (!metadata) return null;

    if (metadata.paystackData) {
      const { paystackData, ...rest } = metadata;
      
      return (
        <div className="space-y-3 sm:space-y-4">
          {paystackData && (
            <div>
              <h5 className="text-[10px] sm:text-xs font-medium text-gray-600 mb-2">Payment Details</h5>
              <div className="bg-[#E8ECF5] rounded-xl divide-y divide-gray-200/50 shadow-neomorph-inset">
                {paystackData.channel && (
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3 flex items-start">
                    <CreditCard className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-[9px] sm:text-xs text-gray-500 block">Payment Method</span>
                      <span className="text-xs sm:text-sm text-gray-800 capitalize break-words">{paystackData.channel}</span>
                    </div>
                  </div>
                )}
                {paystackData.paid_at && (
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3 flex items-start">
                    <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-[9px] sm:text-xs text-gray-500 block">Paid At</span>
                      <span className="text-xs sm:text-sm text-gray-800 break-words">{formatDate(paystackData.paid_at)}</span>
                    </div>
                  </div>
                )}
                {paystackData.gateway_response && (
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3 flex items-start">
                    <Database className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-[9px] sm:text-xs text-gray-500 block">Gateway Response</span>
                      <span className="text-xs sm:text-sm text-gray-800 break-words">{paystackData.gateway_response}</span>
                    </div>
                  </div>
                )}
                {paystackData.customer && paystackData.customer.email && (
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3 flex items-start">
                    <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-[9px] sm:text-xs text-gray-500 block">Customer Email</span>
                      <span className="text-xs sm:text-sm text-gray-800 break-words">{paystackData.customer.email}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (metadata.bankDetails) {
      return (
        <div>
          <h5 className="text-[10px] sm:text-xs font-medium text-gray-600 mb-2">Bank Details</h5>
          <div className="bg-[#E8ECF5] rounded-xl divide-y divide-gray-200/50 shadow-neomorph-inset">
            <div className="px-3 sm:px-4 py-2.5 sm:py-3">
              <span className="text-[9px] sm:text-xs text-gray-500 block">Account Name</span>
              <span className="text-xs sm:text-sm text-gray-800 break-words">{metadata.bankDetails.accountName}</span>
            </div>
            <div className="px-3 sm:px-4 py-2.5 sm:py-3">
              <span className="text-[9px] sm:text-xs text-gray-500 block">Account Number</span>
              <span className="text-xs sm:text-sm text-gray-800 break-words">{metadata.bankDetails.accountNumber}</span>
            </div>
            <div className="px-3 sm:px-4 py-2.5 sm:py-3">
              <span className="text-[9px] sm:text-xs text-gray-500 block">Bank Name</span>
              <span className="text-xs sm:text-sm text-gray-800 break-words">{metadata.bankDetails.bankName}</span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 border border-primary-100/30 w-full max-w-[95%] sm:max-w-2xl">
          <div className="absolute top-0 right-0 pt-3 pr-3 sm:pt-4 sm:pr-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
              <div className="flex items-center mb-1">
                <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Transaction Details
                </h3>
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-6 space-y-4 sm:space-y-6">
            {/* Transaction Overview */}
            <div className="bg-[#E8ECF5] rounded-xl p-4 sm:p-5 shadow-neomorph-inset">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                    transaction.status === 'SUCCESS' ? 'bg-green-100' :
                    transaction.status === 'PENDING' ? 'bg-yellow-100' :
                    transaction.status === 'FAILED' ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    {getStatusIcon(transaction.status)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-medium text-gray-800 break-words">
                      {transaction.description || (transaction.type === 'CREDIT' ? 'Wallet Funding' : 'Wallet Transaction')}
                    </h4>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                      {formatDate(transaction.createdAt)}
                    </p>
                  </div>
                </div>
                <div className="text-left sm:text-right flex-shrink-0">
                  <p className={`text-base sm:text-xl font-bold ${
                    transaction.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {transaction.type === 'CREDIT' ? '+' : '-'}{formatAmount(transaction.amount)}
                  </p>
                  <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-xs font-medium border ${getStatusColor(transaction.status)}`}>
                    {transaction.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Transaction Details */}
            <div className="space-y-3 sm:space-y-4">
              <div>
                <h4 className="text-[10px] sm:text-xs font-medium text-gray-600 mb-2">Transaction Information</h4>
                <div className="bg-[#E8ECF5] rounded-xl divide-y divide-gray-200/50 shadow-neomorph-inset">
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-[9px] sm:text-xs text-gray-500">Transaction ID</span>
                      <div className="flex items-center space-x-2 w-full sm:w-auto">
                        <span className="text-[10px] sm:text-xs font-mono text-gray-800 bg-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-md shadow-neomorph-inset break-all flex-1 sm:flex-none">
                          {transaction.id}
                        </span>
                        <button
                          onClick={() => copyToClipboard(transaction.id, 'Transaction ID')}
                          className="bg-white rounded-md p-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all flex-shrink-0"
                          title="Copy Transaction ID"
                        >
                          <Copy className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-[9px] sm:text-xs text-gray-500">Reference</span>
                      <div className="flex items-center space-x-2 w-full sm:w-auto">
                        <span className="text-[10px] sm:text-xs font-mono text-gray-800 bg-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-md shadow-neomorph-inset break-all flex-1 sm:flex-none">
                          {transaction.reference}
                        </span>
                        <button
                          onClick={() => copyToClipboard(transaction.reference, 'Reference number')}
                          className="bg-white rounded-md p-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all flex-shrink-0"
                          title="Copy Reference"
                        >
                          <Copy className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="px-3 sm:px-4 py-2.5 sm:py-3">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-[9px] sm:text-xs text-gray-500">Type</span>
                      <span className={`inline-flex items-center px-2 py-0.5 sm:py-1 rounded-lg text-[9px] sm:text-xs font-medium w-fit ${
                        transaction.type === 'CREDIT'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {transaction.type}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Formatted Metadata */}
              {transaction.metadata && renderMetadata(transaction.metadata)}

              {/* Paystack Link */}
              {transaction.paystackReference && (
                <div>
                  <h4 className="text-[10px] sm:text-xs font-medium text-gray-600 mb-2">Payment Gateway</h4>
                  <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[9px] sm:text-xs text-gray-500 mb-1">Paystack Reference</p>
                        <p className="text-[10px] sm:text-sm font-mono text-gray-800 break-all">{transaction.paystackReference}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-2">
              <button
                onClick={onClose}
                className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
              >
                Close
              </button>
              {transaction.status === 'FAILED' && (
                <button
                  onClick={() => {
                    // Add retry logic here
                  }}
                  className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-4 py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white"
                >
                  Retry Transaction
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default TransactionDetailsModal;